import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import keyBy from 'lodash/keyBy';
import isEqual from 'lodash/isEqual';
import {
  IMarketStat,
  IMarketStatPayload,
  IMarketStatsBasePayload,
  IMarketStatsData,
} from '@/creditflowresearch/domain/vo/ArticlesAndStats/Stats/IMarketStat';
import { getContainer } from '@framework/di/getContainer';
import CreditFlowResearchRepository from '@/creditflowresearch/infrastructure/repository/CreditFlowResearchRepository';
import {
  initialStatsCollectionFiltersState,
  isAllCategories,
} from '@/creditflowresearch/application/ArticlesAndStats/Stats/constants';
import useFetchCollection from '@/creditflowresearch/infrastructure/state/useFetchCollection';
import { IMarketCategoriesData } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Stats/IMarketCategory';
import { IQueryParams } from '@/creditflowresearch/domain/vo/IQueryParams';
import Market from '@/creditflowresearch/domain/Market';
import Product from '@/creditflowresearch/domain/Product';
import Region from '@/creditflowresearch/domain/Region';
import useDownloadAttachment from '@/creditflowresearch/application/ArticlesAndStats/useDownloadAttachment';
import getStatsFilterFromQuery from '@/creditflowresearch/application/ArticlesAndStats/Stats/serverSide/getStatsFilterFromQuery';
import useCategories from './useCategories';
import { CategoryFilter } from '@/creditflowresearch/ui/components/creditFlow/Stats/config/adapters';
import useAutoFetch from '@/creditflowresearch/application/hooks/useAutoFetch';

interface IUseStats {
  categoriesData: IMarketCategoriesData,
  statsData: IMarketStatsData,
  titleWithPrefix: string,
  filterCategory: CategoryFilter,
}

const useStats = ({ categoriesData, statsData, titleWithPrefix, filterCategory }: IUseStats) => {
  const container = getContainer();
  const { query }:{ query: IQueryParams } = useRouter();
  const { categoryId, products, regions, markets, segments } = query;
  const { collection, totalCount, payload, hasServerError } = statsData;
  const requestService = container.get<CreditFlowResearchRepository>(CreditFlowResearchRepository);
  const [isFirstFetching, setIsFirstFetching] = useState<boolean>(!hasServerError);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [statsPayload, setStatsPayload] = useState<IMarketStatsBasePayload>(payload);
  const { categories } = useCategories(categoriesData);
  const mapCategories = useMemo(() => keyBy(categories, 'id'), [categories]);
  const { downloadAttachment } = useDownloadAttachment();
  const defaultTableTitle = `All ${ titleWithPrefix }`;
  const [tableTitle, setTableTitle] = useState<string>(defaultTableTitle);

  const isExpandedCollection = useRef(false);

  const currentIsExpanded = useRef(false);

  const productIds = useMemo(() => Product.getIds(products), [products]);
  const regionIds = useMemo(() => Region.getIds(regions), [regions]);
  const marketIds = useMemo(() => Market.getIds(markets), [markets]);

  const tabsCategories = useMemo(
    () => categories.filter((category) => filterCategory({ category, productIds, regionIds, marketIds })),
    [categories, productIds, regionIds, marketIds, filterCategory],
  );

  const collectionFilters = initialStatsCollectionFiltersState;

  const fetchCallback = useCallback(async (payload: IMarketStatPayload) => (
    requestService.getStatsList({ ...payload, isExtended: currentIsExpanded.current })
  ), []);

  const statsCollection = useFetchCollection<IMarketStat, IMarketStatPayload>(
    fetchCallback,
    {
      enabled: !isFirstFetching,
      payload: statsPayload as IMarketStatPayload,
      initialValues: { collection, totalCount, ...collectionFilters, debouncedSearchString: collectionFilters.search },
    },
  );

  useAutoFetch(
    () => setIsFirstFetching(false),
    [statsPayload, statsCollection.page, statsCollection.debouncedSearchString],
  );

  useEffect(() => {
    isExpandedCollection.current = isExpanded;
  }, [statsCollection.collection]);

  useEffect(() => {
    currentIsExpanded.current = isExpanded;
  }, [isExpanded]);

  useAutoFetch(() => {
    if (isExpandedCollection.current) {
      return;
    }
    statsCollection.getCollection();
  }, [isExpanded]);

  useEffect(() => statsCollection.reset, []);

  useEffect(() => {
    const categoryIds = isAllCategories(categoryId) ? [] : [categoryId];
    const filter = {
      ...getStatsFilterFromQuery({ products, regions, markets, segments }),
      includedCategoryIds: categoryIds,
    };

    if (isEqual(statsPayload.filter, filter)) {
      return;
    }

    setStatsPayload((prev) => ({
      ...prev,
      filter,
      isExtended: isExpanded,
    }));
  }, [products, regions, markets, segments, categoryId]);

  useEffect(() => {
    if (isAllCategories(categoryId)) {
      setTableTitle(defaultTableTitle);
    } else {
      setTableTitle(mapCategories[categoryId]?.name || defaultTableTitle);
    }
  }, [categoryId, titleWithPrefix]);

  return {
    statsCollection,
    categories: tabsCategories,
    mapCategories,
    downloadAttachment,
    tableTitle,
    isExpanded,
    setIsExpanded,
  };
};

export default useStats;
